import { memo, useEffect, useMemo, useRef, useState } from "react";
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useRadarCaptureDetail } from "hooks/useRadarData";
import {
  RADAR_FIRMWARE,
  RANGE_MAX_MM,
  captureProfile,
  captureReading,
  capturedAt,
  echoList,
  finite,
  floorCheck,
  formatMm as mm,
  levelAt,
  levelDomain,
  levelTicks,
  sgtTime as sgt,
  statusText,
  wallContactDepth,
  withComparison,
} from "utils/radarDiagnostics";

// The echo profile of one radar capture: amplitude against distance with the
// blind zone, the reported distance, the expected floor, the pipe-wall
// contact depth and the sensor's own echo list, optionally over an earlier
// capture. Captures are loaded once each and kept while the panel is open.
const COLORS = {
  profile: "#00897b",
  compare: "#8aa0aa",
  reported: "#eb6834",
  floor: "#2a78d6",
  wall: "#4a3aa7",
  echo: "#172d39",
  blind: "#dfe8eb",
  beyond: "#eef2f4",
};
const TICKS = [0, 250, 500, 750, 1000, 1250, 1500, 1750, 2000];
const STATES = { partial: "partial capture", none: "no profile in this capture" };

// Echo ranks sit on the curve, often right on a marker line: a white halo
// keeps them legible.
const rankLabel =
  (rank) =>
  ({ viewBox }) =>
    viewBox ? (
      <text
        x={viewBox.x + viewBox.width / 2}
        y={viewBox.y - 4}
        textAnchor="middle"
        fontSize={10}
        fontWeight={700}
        fill={COLORS.echo}
        stroke="#ffffff"
        strokeWidth={3}
        strokeLinejoin="round"
        paintOrder="stroke"
      >
        {rank}
      </text>
    ) : null;

// The calibration offset in force when the capture was taken (its own
// context), else the one read back now; null when neither is known.
const offsetOf = (detail, cfg) =>
  finite(detail?.context?.cfg?.cal_mm) ?? finite(cfg?.cal_mm);

const ProfileChart = memo(function ProfileChart({
  rows,
  compared,
  blindMm,
  rangeMm,
  markers,
  echoes,
}) {
  const domain = levelDomain(rows.flatMap((p) => [p.db, p.cmp]));
  const ticks = levelTicks(domain);
  return (
    <div className="field-radar-chart">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={rows}
          margin={{ top: 16, right: 14, bottom: 0, left: 0 }}
        >
          <CartesianGrid stroke="#e8eff1" vertical={false} />
          <XAxis
            dataKey="r"
            type="number"
            domain={[0, RANGE_MAX_MM]}
            ticks={TICKS}
            allowDataOverflow
            minTickGap={10}
            tickFormatter={(v) => v.toLocaleString()}
            tick={{ fontSize: 10, fill: "#6d8490" }}
          />
          <YAxis
            domain={domain}
            ticks={ticks}
            tickFormatter={(v) => Math.round(v)}
            tick={{ fontSize: 10, fill: "#6d8490" }}
            unit=" dB"
            width={62}
          />
          <Tooltip
            labelFormatter={(v) => `${Math.round(v).toLocaleString()} mm`}
            formatter={(value, name) => [
              value === null || value === undefined
                ? "—"
                : `${Number(value).toFixed(1)} dB`,
              name,
            ]}
          />
          {blindMm > 0 && (
            <ReferenceArea
              x1={0}
              x2={Math.min(blindMm, RANGE_MAX_MM)}
              fill={COLORS.blind}
              fillOpacity={0.85}
              ifOverflow="hidden"
            />
          )}
          {rangeMm > 0 && rangeMm < RANGE_MAX_MM && (
            <ReferenceArea
              x1={rangeMm}
              x2={RANGE_MAX_MM}
              fill={COLORS.beyond}
              fillOpacity={0.9}
              ifOverflow="hidden"
            />
          )}
          {compared && (
            <Line
              dataKey="cmp"
              name="Compared capture"
              stroke={COLORS.compare}
              strokeWidth={2}
              strokeDasharray="5 4"
              dot={false}
              activeDot={false}
              isAnimationActive={false}
            />
          )}
          <Line
            dataKey="db"
            name="Echo profile"
            stroke={COLORS.profile}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
          {markers.map((m) => (
            <ReferenceLine
              key={m.key}
              x={m.x}
              stroke={m.color}
              strokeWidth={2}
              strokeDasharray={m.dash}
            />
          ))}
          {echoes.map((e) => (
            <ReferenceDot
              key={e.rank}
              x={e.rangeMm}
              y={e.db}
              r={4.5}
              fill={COLORS.echo}
              stroke="#ffffff"
              strokeWidth={2}
              label={rankLabel(e.rank)}
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
});

function Key({ kind = "solid", color, children }) {
  return (
    <li>
      <span
        className={`field-radar-key is-${kind}`}
        style={color ? { "--key": color } : undefined}
        aria-hidden="true"
      />
      {children}
    </li>
  );
}

export default function RadarProfile({
  captures,
  samples = [],
  refresh = 0,
  cfg = null,
  blindNow = null,
  floorText,
  pipeText,
  onFloorChange,
  onPipeChange,
  focusId = null,
}) {
  const [selectedId, setSelectedId] = useState(null);
  const [compareId, setCompareId] = useState("");
  const cache = useRef(new Map());
  // A capture that just arrived for this browser's request is shown at once.
  useEffect(() => {
    if (focusId !== null && focusId !== undefined) setSelectedId(focusId);
  }, [focusId]);

  const shownId = captures.some((c) => c.id === selectedId)
    ? selectedId
    : captures[0]?.id ?? null;
  const shown = captures.find((c) => c.id === shownId) || null;
  const compared =
    compareId !== "" && String(compareId) !== String(shownId)
      ? captures.find((c) => String(c.id) === String(compareId)) || null
      : null;
  const detail = useRadarCaptureDetail(shownId, refresh, cache);
  const compareDetail = useRadarCaptureDetail(compared?.id ?? null, refresh, cache);
  const offset = offsetOf(detail.data, cfg);
  const profile = useMemo(
    () => captureProfile(detail.data, offset ?? 0),
    [detail.data, offset],
  );
  const compareOffset = offsetOf(compareDetail.data, cfg);
  const comparePoints = useMemo(
    () => captureProfile(compareDetail.data, compareOffset ?? 0).points,
    [compareDetail.data, compareOffset],
  );
  const points = profile.points;
  const rows = useMemo(
    () => (compared ? withComparison(points, comparePoints) : points),
    [compared, points, comparePoints],
  );
  const atCapture = useMemo(
    () => (shown ? captureReading(samples, capturedAt(shown)) : null),
    [samples, shown],
  );
  // The sensor's own distance for this capture (AirHeight), else the reading
  // taken on that wake.
  const reported =
    finite(detail.data?.air_mm ?? shown?.air_mm) ??
    finite(atCapture?.distance_mm);
  const floorMm = finite(floorText) > 0 ? finite(floorText) : null;
  const wall = wallContactDepth(pipeText);
  const blindMm = finite(detail.data?.context?.cfg?.blind_mm) ?? blindNow ?? 0;
  const blindAtCapture = finite(detail.data?.context?.cfg?.blind_mm) !== null;
  const rangeMm =
    finite(detail.data?.context?.cfg?.range_mm) ??
    finite(cfg?.range_mm) ??
    RANGE_MAX_MM;
  const markers = useMemo(
    () =>
      [
        reported !== null &&
          reported <= RANGE_MAX_MM && {
            key: "reported",
            x: reported,
            color: COLORS.reported,
          },
        floorMm !== null &&
          floorMm <= RANGE_MAX_MM && {
            key: "floor",
            x: floorMm,
            color: COLORS.floor,
            dash: "6 4",
          },
        wall !== null &&
          wall <= RANGE_MAX_MM && {
            key: "wall",
            x: wall,
            color: COLORS.wall,
            dash: "2 3",
          },
      ].filter(Boolean),
    [reported, floorMm, wall],
  );
  const dots = useMemo(
    () =>
      (echoList(detail.data?.peaks)?.echoes || [])
        .filter((e) => e.rangeMm >= 0 && e.rangeMm <= RANGE_MAX_MM)
        .map((e) => ({ ...e, db: levelAt(points, e.rangeMm) }))
        .filter((e) => e.db !== null),
    [detail.data, points],
  );
  const check = floorCheck(points, floorMm, blindMm);
  const step = finite(detail.data?.range_step_mm ?? shown?.range_step_mm);
  const peakBin =
    finite(shown?.peak_mm) !== null && step ? Math.round(shown.peak_mm / step) : null;
  const meta = shown
    ? [
        STATES[shown.state] || null,
        shown.bins ? `${shown.bins} bins` : null,
        step
          ? `${mm(step, 1)} apart${detail.data?.range_step_source === "assumed" ? " (assumed)" : ""}`
          : null,
        peakBin !== null ? `the sensor's peak in bin ${peakBin}` : null,
        shown.crc_ok === true
          ? "checksum OK"
          : shown.crc_ok === false
            ? "failed the radar's CRC-16/MODBUS check (the vendor tool would drop this packet)"
            : null,
        profile.dropped > 0 && points.length
          ? `${profile.dropped} bins without a positive amplitude left out`
          : null,
        atCapture
          ? `reading on that wake: ${finite(atCapture.distance_mm) !== null ? mm(atCapture.distance_mm, 1) : "no distance"} (${statusText(atCapture.status)})`
          : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

  return (
    <div className="field-radar-profile">
      <div className="field-radar-profile-head">
        <div>
          <h3 className="field-radar-heading">Echo profile</h3>
          <p className="field-caption" style={{ marginTop: 4 }}>
            Amplitude against distance from the radar, 0–{mm(RANGE_MAX_MM)}
          </p>
        </div>
        <div className="field-radar-toolbar">
          {captures.length > 0 && (
            <>
              <label>
                Capture
                <select
                  value={shownId ?? ""}
                  onChange={(e) =>
                    setSelectedId(
                      captures.find((c) => String(c.id) === e.target.value)
                        ?.id ?? null,
                    )
                  }
                >
                  {captures.map((c) => (
                    <option key={c.id} value={c.id}>
                      {sgt(capturedAt(c))}
                      {STATES[c.state] ? ` · ${STATES[c.state]}` : ""}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Compare with
                <select
                  value={compared ? String(compared.id) : ""}
                  onChange={(e) => setCompareId(e.target.value)}
                  disabled={captures.length < 2}
                >
                  <option value="">None</option>
                  {captures
                    .filter((c) => c.id !== shownId)
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {sgt(capturedAt(c))}
                      </option>
                    ))}
                </select>
              </label>
            </>
          )}
          <label>
            Expected floor (mm)
            <input
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              placeholder="e.g. 1170"
              value={floorText}
              onChange={(e) => onFloorChange(e.target.value)}
            />
          </label>
          <label>
            Pipe inner diameter (mm)
            <input
              type="number"
              inputMode="numeric"
              min={1}
              step={1}
              placeholder="optional"
              value={pipeText}
              onChange={(e) => onPipeChange(e.target.value)}
            />
          </label>
        </div>
      </div>
      {!captures.length ? (
        <div className="field-radar-empty">
          No capture yet - the node needs firmware {RADAR_FIRMWARE}. Capture
          echo profile asks for one on its next wake; it appears here once it
          arrives.
        </div>
      ) : detail.error ? (
        <div className="field-alert" role="alert" style={{ marginTop: 14 }}>
          {detail.error}
        </div>
      ) : !detail.data ? (
        <p className="field-caption">Loading the capture…</p>
      ) : !points.length ? (
        <div className="field-radar-empty">
          This capture holds no usable profile ({meta || "no bins"}). Capture
          again on the next wake.
        </div>
      ) : (
        <>
          <p className="field-caption">{meta}</p>
          <p className="field-radar-axis">
            dB relative to the strongest bin (assuming linear magnitude)
          </p>
          <ProfileChart
            rows={rows}
            compared={!!compared && comparePoints.length > 0}
            blindMm={blindMm}
            rangeMm={rangeMm}
            markers={markers}
            echoes={dots}
          />
          <p className="field-radar-axis is-x">
            {offset !== null
              ? `Distance in mm: the capture's range minus the sensor's ${mm(offset, 1)} calibration offset, as distances are reported`
              : "Range in mm from the radar's own zero: the calibration offset was not read, so reported distances sit lower by that offset"}
          </p>
          <ul className="field-radar-legend" aria-label="Chart key">
            <Key color={COLORS.profile}>
              Echo profile {sgt(capturedAt(shown))}
            </Key>
            {compared && (
              <Key kind="dashed" color={COLORS.compare}>
                {comparePoints.length
                  ? `Compared ${sgt(capturedAt(compared))}, scaled to its own strongest bin`
                  : compareDetail.error
                    ? `Compared capture could not load: ${compareDetail.error}`
                    : compareDetail.data
                      ? "The compared capture holds no usable profile"
                      : "Loading the compared capture…"}
              </Key>
            )}
            {blindMm > 0 && (
              <Key kind="area">
                Blind zone {blindAtCapture ? "at capture" : "now"} 0–
                {mm(blindMm)}
              </Key>
            )}
            {reported !== null && (
              <Key color={COLORS.reported}>
                Reported distance {mm(reported, 1)}
              </Key>
            )}
            {floorMm !== null && (
              <Key kind="dashed" color={COLORS.floor}>
                Expected floor {mm(floorMm)}
              </Key>
            )}
            {wall !== null && (
              <Key kind="dotted" color={COLORS.wall}>
                Beam meets the pipe wall {mm(wall)}
              </Key>
            )}
            {dots.length > 0 && (
              <Key kind="dot">Sensor's echo list (1 = strongest)</Key>
            )}
          </ul>
          {check && (
            <p className="field-caption">
              Strongest echo beyond the blind zone: {mm(check.strongest.r)}.{" "}
              {check.near
                ? `Near the expected floor the highest level is ${(check.strongest.db - check.near.db).toFixed(1)} dB below it, at ${mm(check.near.r)}; the median beyond the blind zone is ${(check.strongest.db - check.median).toFixed(1)} dB below it.`
                : floorMm !== null
                  ? "The expected floor is outside this profile."
                  : "Enter the expected floor to compare the level there."}
              {wall !== null &&
                ` The edge of the ±10° beam meets a ${mm(finite(pipeText))} pipe wall about ${mm(wall)} down; only a joint, slot or ledge there sends an echo back.`}
            </p>
          )}
          <details className="field-radar-details">
            <summary>Capture summary and sensor replies</summary>
            <pre className="field-raw">
              {JSON.stringify(
                {
                  summary: detail.data.summary,
                  context: detail.data.context,
                  byte_size: detail.data.byte_size,
                  amplitude_unit: detail.data.amplitude_unit,
                },
                null,
                2,
              )}
            </pre>
          </details>
        </>
      )}
    </div>
  );
}
